<template>
    <div class="admin-question-dashboard animation-fadeIn">
        <!-- Header Page Section -->
        <div class="page-header">
            <div class="header-info">
                <h2 class="page-title">题目资源库</h2>
                <p class="page-subtitle">全面管理题库中的所有题目，支持精细化筛选、在线编辑与批量入库任务</p>
            </div>
            <div class="header-actions">
                <el-button type="success" plain @click="goToImport">
                    <el-icon>
                        <Upload />
                    </el-icon> 批量导入 (Word)
                </el-button>
                <el-button type="primary" @click="goToEntry">
                    <el-icon>
                        <Plus />
                    </el-icon> 新增单题
                </el-button>
            </div>
        </div>

        <!-- Advanced Filter Card -->
        <el-card class="modern-card filter-card" shadow="never">
            <div class="filter-container">
                <el-row :gutter="16">
                    <el-col :lg="4" :md="8">
                        <el-select v-model="filterType" placeholder="题目类型" @change="loadQuestions" clearable
                            style="width: 100%">
                            <el-option label="单选题" value="1" />
                            <el-option label="多选题" value="6" />
                            <el-option label="填空题" value="2" />
                            <el-option label="解答题" value="3" />
                            <el-option label="证明题" value="4" />
                            <el-option label="应用题" value="5" />
                        </el-select>
                    </el-col>
                    <el-col :lg="4" :md="8">
                        <el-select v-model="filterDifficulty" placeholder="难度等级" @change="loadQuestions" clearable
                            style="width: 100%">
                            <el-option label="容易" value="1" />
                            <el-option label="较易" value="2" />
                            <el-option label="适中" value="3" />
                            <el-option label="较难" value="4" />
                            <el-option label="困难" value="5" />
                        </el-select>
                    </el-col>
                    <el-col :lg="4" :md="8">
                        <el-select v-model="filterGrade" placeholder="年级" @change="loadQuestions" clearable
                            style="width: 100%">
                            <el-option label="高一" value="10" />
                            <el-option label="高二" value="11" />
                            <el-option label="高三" value="12" />
                        </el-select>
                    </el-col>
                    <el-col :lg="8" :md="24">
                        <div class="search-input-group">
                            <el-input v-model="searchKeyword" placeholder="检索题目内容、来源等关键词..." clearable
                                @keyup.enter="loadQuestions">
                                <template #prefix><el-icon>
                                        <Search />
                                    </el-icon></template>
                            </el-input>
                            <el-button type="primary" @click="loadQuestions">搜索</el-button>
                        </div>
                    </el-col>
                </el-row>
            </div>
        </el-card>

        <!-- Main Content Table -->
        <el-card class="modern-card content-card" shadow="never">
            <el-table :data="questions" style="width: 100%" v-loading="loading" class="custom-table">
                <el-table-column prop="id" label="ID" width="80" align="center" />
                <el-table-column label="题目内容" min-width="400">
                    <template #default="scope">
                        <div class="question-preview-box">
                            <div class="question-snippet" v-html="formatSnippet(scope.row.content)"></div>
                            <div class="question-tags"
                                v-if="scope.row.tags?.length || scope.row.knowledgePoints?.length">
                                <el-tag v-for="kp in scope.row.knowledgePoints" :key="'kp-' + kp.id" size="small"
                                    type="primary" plain class="mini-tag">
                                    {{ kp.name }}
                                </el-tag>
                                <el-tag v-for="tag in scope.row.tags" :key="'tag-' + tag.id" size="small" type="success"
                                    plain class="mini-tag">
                                    {{ tag.name }}
                                </el-tag>
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="属性" width="180">
                    <template #default="scope">
                        <div class="meta-stack">
                            <el-tag size="small" :type="getTypeTagType(scope.row.type)">{{ getTypeText(scope.row.type)
                                }}</el-tag>
                            <span class="diff-label">难度: {{ getDifficultyText(scope.row.difficult) }}</span>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="来源信息" width="200">
                    <template #default="scope">
                        <div class="source-info">
                            <div class="val">{{ scope.row.source || '-' }}</div>
                            <div class="year" v-if="scope.row.year">{{ scope.row.year }}年</div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="最后更新" prop="updatedAt" width="160" align="center">
                    <template #default="scope">
                        <span class="time-text">{{ formatDate(scope.row.updatedAt) }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="管理操作" width="140" fixed="right" align="center">
                    <template #default="scope">
                        <el-button link type="primary" @click="editQuestion(scope.row)">编辑</el-button>
                        <el-button link type="danger" @click="deleteQuestion(scope.row)">删除</el-button>
                    </template>
                </el-table-column>
            </el-table>

            <div class="pagination-footer">
                <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize"
                    :page-sizes="[10, 20, 50, 100]" :total="total" layout="total, sizes, prev, pager, next, jumper"
                    @size-change="loadQuestions" @current-change="loadQuestions" />
            </div>
        </el-card>

        <!-- Editing Dialog -->
        <el-dialog v-model="editDialogVisible" title="题目精细化编辑" width="85%" top="4vh" class="modern-dialog"
            destroy-on-close>
            <div class="edit-scroll-body">
                <el-form :model="editForm" label-position="top" class="modern-form">
                    <el-row :gutter="24">
                        <el-col :span="14">
                            <el-form-item label="题干内容 (支持 Markdown / LaTeX)">
                                <MdEditor v-model="editForm.content" preview-theme="cyanosis" style="height: 350px;" />
                            </el-form-item>

                            <!-- Options -->
                            <div v-if="[1, 6].includes(editForm.type)" class="edit-options-block">
                                <div class="block-label">选项管理</div>
                                <div v-for="(opt, index) in editForm.options" :key="index" class="opt-row-entry">
                                    <el-input v-model="opt.label" disabled style="width: 45px" />
                                    <el-input v-model="opt.content" placeholder="选项内容" style="flex: 1" />
                                    <el-button type="danger" link @click="editForm.options.splice(index, 1)"><el-icon>
                                            <Delete />
                                        </el-icon></el-button>
                                </div>
                                <el-button type="primary" plain block @click="addOption"
                                    style="margin-top: 10px; width:100%">+
                                    新增选项</el-button>
                            </div>

                            <el-form-item label="答案" style="margin-top: 20px">
                                <MdEditor v-model="editForm.answer" preview-theme="cyanosis" style="height: 180px;" />
                            </el-form-item>
                            <el-form-item label="解析">
                                <MdEditor v-model="editForm.explanation" preview-theme="cyanosis"
                                    style="height: 180px;" />
                            </el-form-item>
                        </el-col>

                        <el-col :span="10">
                            <el-card shadow="never" class="settings-subcard">
                                <template #header><span class="sub-title">元数据设定</span></template>
                                <el-row :gutter="12">
                                    <el-col :span="12">
                                        <el-form-item label="题型">
                                            <el-select v-model="editForm.type" style="width: 100%">
                                                <el-option label="单选题" :value="1" />
                                                <el-option label="填空题" :value="2" />
                                                <el-option label="解答题" :value="3" />
                                                <el-option label="证明题" :value="4" />
                                                <el-option label="应用题" :value="5" />
                                                <el-option label="多选题" :value="6" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                    <el-col :span="12">
                                        <el-form-item label="难度">
                                            <el-select v-model="editForm.difficult" style="width: 100%">
                                                <el-option label="容易" :value="1" />
                                                <el-option label="较易" :value="2" />
                                                <el-option label="适中" :value="3" />
                                                <el-option label="较难" :value="4" />
                                                <el-option label="困难" :value="5" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                </el-row>
                                <el-row :gutter="12">
                                    <el-col :span="12">
                                        <el-form-item label="年级">
                                            <el-select v-model="editForm.grade" style="width: 100%">
                                                <el-option label="高一" :value="10" />
                                                <el-option label="高二" :value="11" />
                                                <el-option label="高三" :value="12" />
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                    <el-col :span="12">
                                        <el-form-item label="年份">
                                            <el-input v-model="editForm.year" />
                                        </el-form-item>
                                    </el-col>
                                </el-row>
                                <el-form-item label="地区">
                                    <el-cascader v-model="editForm.area" :options="pcTextArr"
                                        :props="{ multiple: true }" placeholder="选择地区" style="width: 100%" clearable
                                        collapse-tags collapse-tags-tooltip />
                                </el-form-item>
                                <el-form-item label="试卷/来源">
                                    <el-input v-model="editForm.source" />
                                </el-form-item>
                            </el-card>

                            <el-card shadow="never" class="settings-subcard" style="margin-top: 20px">
                                <template #header><span class="sub-title">知识点与标签</span></template>
                                <div class="meta-edit-section">
                                    <div class="m-label">关联知识点</div>
                                    <div class="tags-row">
                                        <el-tag v-for="(kp, index) in editForm.knowledgePoints" :key="index" closable
                                            @close="removeKnowledgePoint(index)">
                                            {{ kp.name }}
                                        </el-tag>
                                    </div>
                                    <el-autocomplete v-model="newKpName" :fetch-suggestions="filterKnowledgePoints"
                                        placeholder="搜知识点..." @select="handleSelectKnowledgePoint"
                                        style="width: 100%; margin-top: 8px" />
                                </div>
                                <div class="meta-edit-section" style="margin-top: 16px">
                                    <div class="m-label">特色标签</div>
                                    <div class="tags-row">
                                        <el-tag v-for="(tag, index) in editForm.tags" :key="index" closable
                                            @close="removeTag(index)" type="success">
                                            {{ tag.name }}
                                        </el-tag>
                                    </div>
                                    <el-autocomplete v-model="newTagName" :fetch-suggestions="filterTags"
                                        placeholder="搜标签..." @select="handleSelectTag"
                                        style="width: 100%; margin-top: 8px" />
                                </div>
                            </el-card>
                        </el-col>
                    </el-row>
                </el-form>
            </div>
            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="editDialogVisible = false">舍弃更改</el-button>
                    <el-button type="primary" size="large" @click="saveQuestion" style="width: 120px">保存题目</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Upload, Search, Delete, Edit, CollectionTag } from '@element-plus/icons-vue'
import api from '@/utils/api'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import { pcTextArr } from 'element-china-area-data'

const router = useRouter()
const route = useRoute()

const filterType = ref('')
const filterDifficulty = ref('')
const filterGrade = ref('')
const searchKeyword = ref('')

const currentPage = ref(1)
const pageSize = ref(20)
const total = ref(0)
const questions = ref([])
const loading = ref(false)

const allKnowledgePoints = ref([])
const allTags = ref([])
const newKpName = ref('')
const newTagName = ref('')

const editDialogVisible = ref(false)
const editForm = reactive({
    id: null, content: '', type: 1, difficult: 3, grade: 10,
    year: '', source: '', scene: '', area: [], answer: '', explanation: '',
    knowledgePoints: [], knowledgePointIds: [], tags: [], tagIds: [], options: []
})

const goToImport = () => router.push('/admin/import-questions')
const goToEntry = () => router.push('/admin/question-entry')

const syncKeywordFromRoute = () => {
    const kw = typeof route.query.keyword === 'string' ? route.query.keyword.trim() : ''
    if (kw !== searchKeyword.value) {
        searchKeyword.value = kw
        currentPage.value = 1
    }
}

const loadQuestions = async () => {
    loading.value = true
    try {
        const params = { page: currentPage.value, pageSize: pageSize.value }
        if (filterType.value) params.type = filterType.value
        if (filterDifficulty.value) params.difficult = filterDifficulty.value
        if (filterGrade.value) params.grade = filterGrade.value
        if (searchKeyword.value) params.keyword = searchKeyword.value

        const res = await api.get('/api/questions', { params })
        questions.value = res.data.questions || []
        total.value = res.data.total || 0
    } catch (e) { ElMessage.error('加载失败') } finally { loading.value = false }
}

const fetchMetas = async () => {
    try {
        const [kpRes, tagRes] = await Promise.all([api.get('/api/knowledgePoints'), api.get('/api/tags')])
        allKnowledgePoints.value = kpRes.data.knowledgePoints || kpRes.data || []
        allTags.value = tagRes.data || []
    } catch (e) { }
}

const filterKnowledgePoints = (queryString, cb) => {
    const results = queryString ? allKnowledgePoints.value.filter(kp => kp.name.toLowerCase().includes(queryString.toLowerCase())) : allKnowledgePoints.value
    cb(results.map(kp => ({ value: kp.name, id: kp.id })))
}
const filterTags = (queryString, cb) => {
    const results = queryString ? allTags.value.filter(t => t.name.toLowerCase().includes(queryString.toLowerCase())) : allTags.value
    cb(results.map(t => ({ value: t.name, id: t.id })))
}

const handleSelectKnowledgePoint = (item) => {
    if (!editForm.knowledgePointIds.includes(item.id)) {
        editForm.knowledgePoints.push({ id: item.id, name: item.value })
        editForm.knowledgePointIds.push(item.id)
    }
    newKpName.value = ''
}
const handleSelectTag = (item) => {
    if (!editForm.tagIds.includes(item.id)) {
        editForm.tags.push({ id: item.id, name: item.value })
        editForm.tagIds.push(item.id)
    }
    newTagName.value = ''
}

const removeKnowledgePoint = (idx) => { editForm.knowledgePoints.splice(idx, 1); editForm.knowledgePointIds.splice(idx, 1) }
const removeTag = (idx) => { editForm.tags.splice(idx, 1); editForm.tagIds.splice(idx, 1) }

const addOption = () => {
    const label = String.fromCharCode(65 + editForm.options.length)
    editForm.options.push({ label, content: '' })
}

const formatSnippet = (c) => c ? c.replace(/[#*`\n]/g, ' ').substring(0, 120) + (c.length > 120 ? '...' : '') : '-'
const getTypeText = (t) => ({ 1: '单选题', 2: '填空题', 3: '解答题', 4: '证明题', 5: '应用题', 6: '多选题' }[t] || t)
const getTypeTagType = (t) => ({ 1: '', 2: 'success', 3: 'warning', 6: 'info' }[t] || 'danger')
const getDifficultyText = (d) => ({ 1: '容易', 2: '较易', 3: '适中', 4: '较难', 5: '困难' }[d] || d)
const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-'

const editQuestion = (row) => {
    Object.assign(editForm, {
        id: row.id, content: row.content || '', type: row.type || 1, difficult: row.difficult || 3,
        grade: row.grade || 10, year: row.year || '', source: row.source || '', answer: row.answer || '', explanation: row.explanation || '',
        knowledgePoints: (row.knowledgePoints || []).map(k => ({ id: k.id, name: k.name })),
        knowledgePointIds: (row.knowledgePoints || []).map(k => k.id),
        tags: (row.tags || []).map(t => ({ id: t.id, name: t.name })),
        tagIds: (row.tags || []).map(t => t.id),
        options: (row.options || []).map(o => ({ label: o.label, content: o.content }))
    })
    // Area parsing
    if (row.region) {
        const regions = row.region.split('|').filter(Boolean); const cities = (row.city || '').split('|')
        editForm.area = regions.map((r, i) => cities[i] ? [r, cities[i]] : [r])
    } else editForm.area = []
    editDialogVisible.value = true
}

const saveQuestion = async () => {
    try {
        const regionStr = editForm.area.map(a => a[0] || '').filter(Boolean).join('|')
        const cityStr = editForm.area.map(a => a[1] || '').join('|')
        await api.put(`/api/questions/${editForm.id}`, { ...editForm, region: regionStr, city: cityStr })
        ElMessage.success('更新成功')
        editDialogVisible.value = false
        loadQuestions()
    } catch (e) { ElMessage.error('保存失败') }
}

const deleteQuestion = (row) => {
    ElMessageBox.confirm('永久删除该题目？', '警告', { type: 'error' }).then(async () => {
        await api.delete(`/api/questions/${row.id}`)
        ElMessage.success('已移除')
        loadQuestions()
    }).catch(() => { })
}

const checkEditId = async () => {
    const editId = route.query.editId
    if (editId) {
        try {
            const res = await api.get(`/api/questions/${editId}`)
            if (res.data) {
                editQuestion(res.data)
            }
        } catch (e) {
            ElMessage.error('无法加载指定题目的编辑信息')
        }
    }
}

onMounted(() => {
    syncKeywordFromRoute()
    loadQuestions(); fetchMetas()
    checkEditId()
})

watch(() => route.query.editId, (newId) => {
    if (newId) checkEditId()
    else editDialogVisible.value = false
})

watch(() => route.query.keyword, () => {
    syncKeywordFromRoute()
    loadQuestions()
})
</script>

<style scoped>
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
}

.page-subtitle {
    font-size: 14px;
    color: #606266;
    margin-top: 4px;
}

.modern-card {
    border: none;
    border-radius: 12px;
}

.filter-card {
    margin-bottom: 24px;
}

.search-input-group {
    display: flex;
    gap: 8px;
}

.custom-table {
    border-radius: 12px;
    overflow: hidden;
}

.question-preview-box {
    padding: 8px 0;
}

.question-snippet {
    color: #333;
    font-size: 14px;
    margin-bottom: 8px;
    line-height: 1.6;
}

.question-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.mini-tag {
    height: 20px;
    font-size: 11px;
}

.meta-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.diff-label {
    font-size: 12px;
    color: #909399;
}

.source-info {
    font-size: 13px;
    color: #606266;
}

.source-info .year {
    font-size: 11px;
    color: #909399;
    margin-top: 4px;
}

.time-text {
    font-size: 12px;
    color: #909399;
}

.pagination-footer {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

.edit-scroll-body {
    max-height: 75vh;
    overflow-y: auto;
    padding: 0 10px;
}

.settings-subcard {
    border: 1px solid #f0f0f0;
    background: #fafafa;
    border-radius: 10px;
}

.sub-title {
    font-weight: 700;
    font-size: 14px;
}

.opt-row-entry {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
    align-items: center;
}

.m-label {
    font-size: 12px;
    font-weight: 700;
    color: #909399;
    margin-bottom: 8px;
}

.tags-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-height: 32px;
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
